import { standingOf } from '@novachat/client';
import type { Channel, Me } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useLive } from '../state/live';
import { uploadChannelLogo } from '../lib/upload';
import { ChannelIcon } from './ChannelIcon';
import { closeOnBackdrop } from '../lib/dismiss';

export function Modal({
  title,
  onClose,
  children,
  size = 'w-[min(440px,calc(100vw-24px))]',
}: {
  title: string;
  onClose: () => void;
  children: (close: () => void) => ReactNode;
  /** Classes for its size (and layout); 440px wide by default. */
  size?: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => dialog.current?.showModal(), []);
  const close = () => dialog.current?.close();
  return (
    <dialog onClick={closeOnBackdrop} ref={dialog} onClose={onClose} aria-labelledby={titleId} className={`m-auto ${size} rounded-xl border border-line-strong bg-surface-1 p-6 text-fg-1 backdrop:bg-black/60`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 id={titleId} className="m-0 min-w-0 truncate font-heading text-xl font-bold">
          {title}
        </h2>
        <button type="button" aria-label="Kapat" onClick={close} className="grid size-8 flex-none place-items-center rounded-sm border-0 bg-transparent text-fg-2 hover:bg-surface-2">
          <X size={18} />
        </button>
      </div>
      {children(close)}
    </dialog>
  );
}

const field = 'h-[42px] rounded-md border border-line-strong bg-surface-2 px-3 text-base text-fg-1 placeholder:text-fg-3 focus:border-brand-400 focus:outline-none sm:text-[15px]';
const ErrorText = ({ text }: { text: string | null }) => (text ? <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">{text}</p> : null);

/** Upload or remove the channel's logo; the sidebar follows through the channel.changed event. */
function LogoField({ channel, onError }: { channel: Channel; onError: (message: string | null) => void }) {
  const queries = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    onError(null);
    try {
      await action();
      await queries.invalidateQueries({ queryKey: ['channels'] });
    } catch (e) {
      onError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const button = 'nc-press h-9 rounded-md border border-line-strong bg-surface-2 px-3 text-sm font-semibold disabled:opacity-60';
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-14 place-items-center rounded-[16px] bg-surface-2 text-fg-2">
        <ChannelIcon channel={channel} size={channel.logoUrl ? 56 : 24} />
      </span>
      <button type="button" disabled={busy} onClick={() => picker.current?.click()} className={button}>
        {busy ? 'Yükleniyor…' : channel.logoUrl ? 'Logoyu değiştir' : 'Logo yükle'}
      </button>
      {channel.logoUrl && (
        <button type="button" disabled={busy} onClick={() => void run(() => client.api.removeChannelLogo(channel.id))} className={`${button} text-[#EE9191]`}>
          Kaldır
        </button>
      )}
      <input
        ref={picker}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void run(() => uploadChannelLogo(channel.id, file));
        }}
      />
    </div>
  );
}

export function EditRoomDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const queries = useQueryClient();
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic ?? '');
  const [error, setError] = useState<string | null>(null);

  async function save(event: FormEvent, close: () => void) {
    event.preventDefault();
    if (!name.trim()) return setError('Kanala bir ad ver.');
    try {
      await client.api.updateChannel(channel.id, { name: name.trim(), topic: topic.trim() || null });
      await queries.invalidateQueries({ queryKey: ['channels'] });
      close();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Modal title="Kanalı düzenle" onClose={onClose}>
      {(close) => (
        <form onSubmit={(e) => void save(e, close)} className="flex flex-col gap-4" noValidate>
          <LogoField channel={channel} onError={setError} />
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-fg-2">Kanal adı</span>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} className={field} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-fg-2">Konu</span>
            <input value={topic} maxLength={300} onChange={(e) => setTopic(e.target.value)} placeholder="Örn. API ve deploy konuşmaları" className={field} />
          </label>
          <ErrorText text={error} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={close} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
              Vazgeç
            </button>
            <button type="submit" className="nc-press nc-cta h-10 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white">
              Kaydet
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function ArchiveDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const queries = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => dialog.current?.showModal(), []);
  async function archive() {
    try {
      await client.api.archiveChannel(channel.id);
      await queries.invalidateQueries({ queryKey: ['channels'] });
      useLive.getState().select(null);
      dialog.current?.close();
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  return (
    <dialog onClick={closeOnBackdrop} ref={dialog} onClose={onClose} className="m-auto w-[min(420px,calc(100vw-32px))] rounded-xl border border-line-strong bg-surface-1 p-6 text-fg-1 backdrop:bg-black/60">
      <h2 className="mt-0 mb-2 font-heading text-xl font-bold">“{channel.name}” arşivlensin mi?</h2>
      <p className="mt-0 text-sm leading-normal text-fg-2">
        Kanal herkesin listesinden kalkar ve görüşmedekiler bağlantıdan düşer. Mesajlar silinmez: kanalı arşivleyebilenler kanal listesinin altındaki “Arşivlenen kanallar”dan geri açabilir.
      </p>
      {error && <p role="alert" className="text-sm text-[#EE9191]">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={() => dialog.current?.close()} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
          Vazgeç
        </button>
        <button type="button" onClick={() => void archive()} className="nc-press h-10 rounded-md border-0 bg-red-400 px-4 text-sm font-bold text-white">
          Arşivle
        </button>
      </div>
    </dialog>
  );
}

/** "Kanalı sil": for good, so the channel's name is typed first and a slip of the hand cannot do it. */
export function DeleteChannelDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const queries = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const nameId = useId();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => dialog.current?.showModal(), []);
  const confirmed = typed.trim() === channel.name.trim();
  async function remove(event: FormEvent) {
    event.preventDefault();
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      await client.api.deleteChannel(channel.id);
      // Leave it before the list refreshes: its messages no longer exist to be read.
      useLive.getState().select(null);
      await queries.invalidateQueries({ queryKey: ['channels'] });
      dialog.current?.close();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }
  return (
    <dialog onClick={closeOnBackdrop} ref={dialog} onClose={onClose} aria-labelledby={titleId} className="m-auto w-[min(440px,calc(100vw-32px))] rounded-xl border border-line-strong bg-surface-1 p-6 text-fg-1 backdrop:bg-black/60">
      <form onSubmit={(e) => void remove(e)} noValidate>
        <h2 id={titleId} className="mt-0 mb-2 font-heading text-xl font-bold break-words">“{channel.name}” kalıcı olarak silinsin mi?</h2>
        <p className="mt-0 text-sm leading-normal text-fg-2">
          Kanaldaki bütün mesajlar, yanıtlar ve paylaşılan dosyalar herkes için silinir; görüşmedekiler bağlantıdan düşer. <strong className="text-fg-1">Bu işlem geri alınamaz.</strong> Mesajları saklamak istiyorsan kanalı arşivle.
        </p>
        <label htmlFor={nameId} className="mt-4 mb-1.5 block text-[13px] font-semibold text-fg-2">
          Onaylamak için kanalın adını yaz: <span className="break-all text-fg-1">{channel.name}</span>
        </label>
        <input id={nameId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} placeholder={channel.name} className={`${field} w-full`} />
        {error && <p role="alert" className="text-sm text-[#EE9191]">{error}</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => dialog.current?.close()} className="nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold">
            Vazgeç
          </button>
          <button type="submit" disabled={!confirmed || busy} className="nc-press h-10 rounded-md border-0 bg-red-500 px-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-45">
            Kalıcı olarak sil
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * "Kanaldan ayrıl" from the channel menu: asks first, and says what leaving means here. The only
 * owner cannot leave (docs/plans/2026-11-03-channel-roles-design.md): they are told why, with the two ways out.
 */
export function LeaveDialog({ channel, me, onClose, onPeople, onArchive }: { channel: Channel; me: Pick<Me, 'id'>; onClose: () => void; onPeople: () => void; onArchive: () => void }) {
  const queries = useQueryClient();
  const roles = useQuery({ queryKey: ['roles', channel.id], queryFn: () => client.api.roles(channel.id) });
  const [error, setError] = useState<string | null>(null);
  const standing = roles.data ? standingOf(channel, me, roles.data) : null;
  const sole = !!standing && standing.isOwner(me.id) && standing.owners === 1;

  async function leave(close: () => void) {
    setError(null);
    try {
      await client.api.removeMember(channel.id, me.id);
      await queries.invalidateQueries({ queryKey: ['channels'] });
      useLive.getState().select(null);
      close();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  const button = 'nc-press h-10 rounded-md border border-line-strong bg-surface-2 px-4 text-sm font-semibold';
  return (
    <Modal title={sole ? 'Bu kanalın tek sahibisin' : `${channel.name} kanalından ayrılsın mı?`} onClose={onClose}>
      {(close) =>
        !standing ? (
          <p className="m-0 text-sm text-fg-3">{roles.isError ? errorMessage(roles.error) : 'Yükleniyor…'}</p>
        ) : sole ? (
          <div className="flex flex-col gap-4">
            <p className="m-0 text-sm leading-normal text-fg-2">
              Kanalda sahipsiz kalmaması için ayrılamazsın. Önce birine <span className="font-semibold text-fg-1">Sahip</span> rolünü ver; sonra ayrılabilirsin. Kanal artık gerekmiyorsa arşivleyebilirsin.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              {channel.permissions.includes('delete_channel') && (
                <button type="button" onClick={onArchive} className={`${button} text-[#EE9191]`}>
                  Kanalı arşivle
                </button>
              )}
              <button type="button" onClick={onPeople} className="nc-press nc-cta h-10 rounded-md border-0 bg-brand-500 px-4 text-sm font-bold text-white">
                Sahiplik ver
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="m-0 text-sm leading-normal text-fg-2">
              {channel.visibility === 'public'
                ? 'Kanal listenden kalkar; mesajları ve bildirimleri artık gelmez. İstediğin zaman kenar çubuğundaki “Ayrıldığın kanallar”dan geri katılabilirsin.'
                : 'Kanal listenden kalkar. Geri dönmek için kanaldan birinin seni yeniden eklemesi gerekir.'}
              {standing.rolesOf(me.id).length > 0 && ' Bu kanaldaki rollerin de bırakılır.'}
            </p>
            {error && <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className={button}>
                Vazgeç
              </button>
              <button type="button" onClick={() => void leave(close)} className="nc-press h-10 rounded-md border-0 bg-red-400 px-4 text-sm font-bold text-white">
                Ayrıl
              </button>
            </div>
          </div>
        )
      }
    </Modal>
  );
}
